import { LOCALES } from "../vars/Languages";
import { UrlParam } from "../vars/Urls";

export class URLs {
  public static localizeHref(href: string, pathname: string): string {
    if (!href || href === "#") {
      return href;
    }

    const currentLocale = pathname.split("/").filter(Boolean)[0];

    if (!currentLocale || !LOCALES.includes(currentLocale)) {
      return href;
    }

    const replaceLeadingLocale = (path: string): string => {
      const segments = path.split("/");
      const index = segments[0] === "" ? 1 : 0;

      if (LOCALES.includes(segments[index])) {
        segments[index] = currentLocale;
        return segments.join("/");
      }

      return path;
    };

    if (href.includes("://")) {
      try {
        const url = new URL(href);
        url.pathname = replaceLeadingLocale(url.pathname);
        return url.toString();
      } catch (_e) {
        return href;
      }
    }

    if (href.startsWith("/")) {
      return replaceLeadingLocale(href);
    }

    return href;
  }

  public static isSecure(url: string): boolean {
    return url.startsWith("https://");
  }

  public static isLinkedIn(url: string): boolean {
    return url.startsWith("https://linkedin.com/") || url.startsWith("https://www.linkedin.com/");
  }

  public static isLinkedInCdn(url: string): boolean {
    return url.startsWith("https://media.licdn.com/");
  }

  public static getServerUrl(lang: string = ""): string {
    let url: string = "";

    if (typeof window !== "undefined") {
      url = window.location.origin;
    }

    if (process.env.VERCEL_URL) {
      url = `https://${process.env.VERCEL_URL}`;
    }

    if (process.env.NEXT_PUBLIC_APP_URL) {
      url = process.env.NEXT_PUBLIC_APP_URL;
    }

    if (!url) {
      url = "http://localhost:3000";
    }

    if (lang && !url.endsWith("/")) {
      url += "/";
    }

    if (lang) {
      url += lang;
    }

    return url;
  }

  // The path (+ query/hash) of an absolute app URL, for client-side navigation that must
  // stay on the current host (e.g. a company subdomain) instead of the canonical domain.
  public static pathOf(url: string): string {
    try {
      const u = new URL(url);
      return `${u.pathname}${u.search}${u.hash}`;
    } catch (_e) {
      return url;
    }
  }

  // A client-supplied return path, reduced to something that can only point back into the app.
  // Anything absolute or protocol-relative would turn a Stripe return_url into an open redirect.
  public static safeReturnPath(path?: string | null): string {
    if (!path?.startsWith("/") || path.startsWith("//")) {
      return "/";
    }
    return path;
  }

  public static getTenantServerUrl(slug: string, lang: string = ""): string {
    try {
      const u = new URL(URLs.getServerUrl());
      const host = u.hostname.replace(/^www\./, "");
      u.hostname = `${slug}.${host}`;
      let url = u.origin;
      if (lang) {
        url += `/${lang}`;
      }
      return url;
    } catch (_e) {
      return URLs.getServerUrl(lang);
    }
  }

  public static addUTM(url: string, utmSource: string = "meidly.com"): string {
    if (!url || url === "#") return url;

    try {
      const u = new URL(url);
      u.searchParams.set("utm_source", utmSource);
      return u.toString();
    } catch (_e) {
      // Fallback for relative URLs or invalid URLs if necessary
      if (url.startsWith("/") || url.startsWith(".")) {
        const separator = url.includes("?") ? "&" : "?";
        return `${url}${separator}utm_source=${utmSource}`;
      }
      return url;
    }
  }

  public static isPathActive(
    currentPathname: string,
    targetHref: string,
    homeHref: string,
  ): boolean {
    const getPath = (url: string) => {
      if (url.includes("://")) {
        try {
          return new URL(url).pathname;
        } catch (_e) {
          return url;
        }
      }
      return url;
    };

    const p = currentPathname.replace(/\/$/, "");
    const h = URLs.localizeHref(getPath(targetHref), currentPathname).replace(/\/$/, "");
    const home = URLs.localizeHref(getPath(homeHref), currentPathname).replace(/\/$/, "");

    if (h === home) return p === home;
    return p === h || p.startsWith(`${h}/`);
  }

  public static getAffiliateLink(code: string): string {
    return `${URLs.getServerUrl()}?${UrlParam.REFERRAL_CODE}=${code}`;
  }

  public static extensionStore(): string {
    return `https://chromewebstore.google.com/detail/${process.env.NEXT_PUBLIC_EXTENSION_ID}`;
  }
}
